import React, { useState, useEffect, RefObject, useRef } from 'react';
import { UploadInfo } from '../../libs/gs-component-image-upload';
import Icon, { IconButton } from '@xo-union/tk-component-icons';
import { ConfirmationModal } from '../../libs/gs-component-form';

import styles from './coverPhotoPortrait.scss';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import { resizeImagePortrait } from '../../helpers/mediaApiUrls';
import { imageUrlToImgElement } from '../../helpers/images/processImages';

import gql from 'graphql-tag';
import {
  Wws_CreatePhotoItemAttrs,
  Wws_UpdatePhotoItemAttrs,
  useCreateCoverPhotoItemMutation,
  useUpdateCoverPhotoItemMutation,
  useDeleteCoverPhotoItemMutation,
  CoverPhotoItemsFragment,
  CoverPhotoItemsFragmentDoc,
  PageQuery,
  Maybe,
} from '../../generated/graphql';
import { pageQuery } from '../PageItemsRail/graphql';
import { useImageUploader } from '../../helpers/images/useImageUploader';
import { Page } from '../PageCard';
import PhotoUploadModal from '../Modals/PhotoUploadModal';
import focusedImage from '../../helpers/focusedImage';
import PhotoUploadSuggestion from '../Modals/PhotoUploadModal/PhotoUploadSuggestion';

const minResolution = '(min-resolution: 96dpi)';
const minPixelRatio = '(-webkit-min-device-pixel-ratio: 1)';
const sizes = {
  xs: `${minResolution} and (min-width: 0), ${minPixelRatio} and (min-width: 0)`,
  sm: `${minResolution} and (min-width: 480px), ${minPixelRatio} and (min-width: 480px)`,
  md: `${minResolution} and (min-width: 768px), ${minPixelRatio} and (min-width: 768px)`,
};

const getPictureSource = (url: Maybe<string>): { srcSet: string; media: string } => {
  const widthSizes = [600, 1000, 600];
  const sourceSet = widthSizes.map(w => resizeImagePortrait(url, w));
  const srcSet = sourceSet.join(', ');
  const mediaSet = Object.values(sizes);
  const media = mediaSet.join(', ');
  return { srcSet, media };
};

export const createCoverPhotoItemMutation = gql`
  mutation createCoverPhotoItemMutation($attributes: WWS_CreatePhotoItemAttrs!) {
    coverPhotoItem: createCoverPhotoItem(attributes: $attributes) {
      id
      cropX
      cropY
      focalPoint
      height
      mediaApiId
      mediaUrl
      rank
      rotation
      type
      width
      date
      caption
      aspectRatio {
        name
        value
      }
    }
  }
`;

export const updateCoverPhotoItemMutation = gql`
mutation updateCoverPhotoItemMutation($attributes: WWS_UpdatePhotoItemAttrs!) {
  coverPhotoItem: updateCoverPhotoItem(attributes: $attributes) {
    id
    ...CoverPhotoItems
    ${CoverPhotoItemsFragmentDoc}
  }
}
`;

export const deleteCoverPhotoItemMutation = gql`
  mutation deleteCoverPhotoItemMutation($attributes: WWS_DeletePageItemAttributes!) {
    deletePageItem(attributes: $attributes)
  }
`;

interface Props {
  page: Page;
  iframeRef?: RefObject<HTMLIFrameElement>;
}

type CoverPhotoItemType = CoverPhotoItemsFragment | undefined;

export interface SaveImageProps {
  file: any;
  height: number;
  rotation: number;
  width: number;
  focalPoint: string[] | null;
}

const updatePageInIFrame = (iframeRef: RefObject<HTMLIFrameElement>, page: Page) => {
  iframeRef?.current?.contentWindow?.postMessage(
    JSON.stringify({
      type: 'updatePage',
      page,
    }),
    getTargetOrigin()
  );
};

const CoverPhotoPortrait: React.FC<Props> = ({ page, iframeRef }) => {
  const pageId = page.id;
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<HTMLImageElement>();
  const [loadingUpdatedImage, setloadingUpdatedImage] = useState(false);
  const [createCoverPhotoItem] = useCreateCoverPhotoItemMutation();
  const [updateCoverPhotoItem] = useUpdateCoverPhotoItemMutation();
  const [deleteCoverPhotoItem] = useDeleteCoverPhotoItemMutation();
  const coverPhotoItem = page.items.find(i => i.type === 'CoverPhotoItem') as CoverPhotoItemType;
  const [imageData, setImageData] = useState<CoverPhotoItemType>(coverPhotoItem);
  const coverPhotoRef = useRef<HTMLImageElement>(null);
  const alertRef = useRef<any>(null);

  const focalPoint = coverPhotoItem?.focalPoint;
  const xfocalPoint = focalPoint ? parseFloat(focalPoint[0]) : 0;
  const yfocalPoint = focalPoint ? parseFloat(focalPoint[1]) : 0;

  focusedImage(coverPhotoRef, xfocalPoint, yfocalPoint);

  // reapplies focusedImage when opening and closing page cards
  useEffect(() => {
    focusedImage(coverPhotoRef, xfocalPoint, yfocalPoint);
  }, [xfocalPoint, yfocalPoint]);

  useEffect(() => {
    setImageData(coverPhotoItem);
  }, [coverPhotoItem]);

  const { imageUpload } = useImageUploader();

  const closeModal = () => {
    setUploadFile(undefined);
  };

  const openModal = async (uploadInfo: UploadInfo) => {
    const { imagePreviewUrl } = uploadInfo;
    if (!imagePreviewUrl || typeof imagePreviewUrl !== 'string') return;

    const img = await imageUrlToImgElement(imagePreviewUrl);
    setUploadFile(img);
    setInfoModalOpen(!infoModalOpen);
  };

  const showError = (message?: string) => {
    if (!alertRef.current) return;
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message || 'There was an error uploading your image. Try again.',
      waitTime: 3000,
    });
  };

  const editImage = async () => {
    if (!imageData?.mediaUrl) return;

    const img = await imageUrlToImgElement(imageData.mediaUrl);
    setUploadFile(img);
  };

  const createOrUpdateCoverPhotoItem = async (imageAttrs: Wws_CreatePhotoItemAttrs | Wws_UpdatePhotoItemAttrs) => {
    if (imageData && imageData.id) {
      return updateCoverPhotoItem({
        variables: {
          attributes: { ...imageAttrs, rotation: 0, id: imageData.id } as Wws_UpdatePhotoItemAttrs,
        },
        update(cache, { data }) {
          const { page } = cache.readQuery<PageQuery>({ query: pageQuery, variables: { id: pageId } })!;
          const updatedHomePage = {
            ...page,
            items: page.items.map(item => (item.id === data?.coverPhotoItem?.id ? updateCoverPhotoItem : item)),
          };

          if (iframeRef) {
            updatePageInIFrame(iframeRef, updatedHomePage as Page);
          }
        },
      });
    } else {
      return createCoverPhotoItem({
        variables: {
          attributes: { ...(imageAttrs as Wws_CreatePhotoItemAttrs), rotation: 0 },
        },
        update(cache, { data }) {
          const { page } = cache.readQuery<PageQuery>({ query: pageQuery, variables: { id: pageId } })!;
          const updatedHomePage = {
            ...page,
            items: [...page.items, data?.coverPhotoItem],
          };

          cache.writeQuery({
            query: pageQuery,
            data: {
              page: updatedHomePage,
            },
          });

          if (iframeRef) {
            updatePageInIFrame(iframeRef, updatedHomePage as Page);
          }
        },
      });
    }
  };

  const saveImage = async ({ file, height, width, focalPoint }: SaveImageProps) => {
    try {
      setloadingUpdatedImage(true);
      const res = await imageUpload({ file });

      const imageAttrs = {
        mediaApiId: res?.data?.mediaApiId,
        pageId,
        height,
        width,
        focalPoint,
      };
      const { data } = await createOrUpdateCoverPhotoItem(imageAttrs);

      if (data) setImageData(data?.coverPhotoItem);
      setloadingUpdatedImage(false);
      closeModal();
    } catch (err) {
      showError();
      setloadingUpdatedImage(false);
    }
  };

  const deleteImage = async () => {
    if (!imageData) return;
    setloadingUpdatedImage(true);
    setDeleteConfirmationOpen(false);

    await deleteCoverPhotoItem({
      variables: {
        attributes: {
          id: imageData.id,
          pageId: page.id,
        },
      },
      update(cache, { data }) {
        const { page } = cache.readQuery<PageQuery>({ query: pageQuery, variables: { id: pageId } })!;
        const updatedHomePage = {
          ...page,
          items: page.items.filter(item => item.id !== data?.deletePageItem),
        };

        cache.writeQuery({
          query: pageQuery,
          data: {
            page: updatedHomePage,
          },
        });
        setloadingUpdatedImage(false);
        setImageData(undefined);
        if (iframeRef) {
          updatePageInIFrame(iframeRef, updatedHomePage as Page);
        }
      },
    });
  };

  return (
    <>
      <ConfirmationModal
        open={deleteConfirmationOpen}
        headerText="Are you sure?"
        bodyText="This will permanently erase the cover photo from your Website."
        cancelText="No, Cancel"
        confirmText="Yes, Continue"
        onConfirm={deleteImage}
        onCancel={() => setDeleteConfirmationOpen(false)}
      />
      {imageData?.mediaUrl ? (
        <div data-testid="cover-photo" className={styles.imageContainer}>
          <picture>
            <source {...getPictureSource(imageData.mediaUrl)} />
            <img
              ref={coverPhotoRef}
              src={resizeImagePortrait(imageData.mediaUrl, 600)}
              alt={imageData.caption || 'cover photo'}
              data-focus-x={xfocalPoint}
              data-focus-y={yfocalPoint}
            />
          </picture>
          <div className={styles.overlay}>
            <div className={`${styles.coverAction}`} onClick={editImage} data-testid="edit-cp">
              <div className={`${styles.coverIcon} ${styles.uploadIcon}`}>
                <Icon size="lg" name="edit" />
              </div>
              <div className={`${styles.coverIconLabel}`}>Edit</div>
            </div>
            <div
              className={`${styles.coverAction}`}
              onClick={() => setDeleteConfirmationOpen(!deleteConfirmationOpen)}
              data-testid="delete-cp"
            >
              <div className={`${styles.coverIcon}`}>
                <IconButton size="lg" name="delete" aria-label="delete" />
              </div>
              <div className={`${styles.coverIconLabel}`}>Delete</div>
            </div>
          </div>
        </div>
      ) : (
        <div
          data-testid="empty-cover-photo"
          className={styles.emptyImageContainer}
          onClick={() => setInfoModalOpen(!infoModalOpen)}
        >
          <div className={styles.photoCta}>
            <Icon className={styles.icon} size="md" name="add_circle" />
            <div>Add a Cover Photo</div>
          </div>
        </div>
      )}
      {uploadFile && (
        <PhotoUploadModal
          ref={alertRef}
          saveImage={saveImage}
          uploadFile={uploadFile}
          onCancel={closeModal}
          titleText={imageData?.mediaUrl ? 'Edit Photo' : 'Upload Photo'}
          primaryActionText={imageData?.mediaUrl ? 'Save Photo' : 'Upload Photo'}
          secondaryActionText="Cancel"
          loading={loadingUpdatedImage}
          focalPoint={imageData?.focalPoint}
        />
      )}
      {infoModalOpen && (
        <PhotoUploadSuggestion
          openUploadModal={openModal}
          setInfoModalOpen={setInfoModalOpen}
          infoModalOpen={infoModalOpen}
        />
      )}
    </>
  );
};

export default CoverPhotoPortrait;
